import {
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from '../firebase'
import {
  HELPER_TAB_KEYS,
  MEMBER_TAB_KEYS,
  type TabKey,
} from './tabCatalog'
import type { WorkspaceTabDefaults } from './tabBarEngine'
import { stripUndefinedDeep } from '../syncSanitize'

export type { WorkspaceTabDefaults }

export interface TabPrefs {
  member?: TabKey[]
  helper?: TabKey[]
  updatedAt?: unknown
}

const STORAGE_KEY_PREFIX = 'timelining.tabPrefs'

const inMemoryCache = new Map<string, string>()

function getStorageKey(uid: string, workspaceId: string): string {
  return `${STORAGE_KEY_PREFIX}.${uid}.${workspaceId}`
}

function getStorageItem(key: string): string | null {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      return window.localStorage.getItem(key)
    } catch {
      // ignore
    }
  }
  return inMemoryCache.get(key) ?? null
}

function setStorageItem(key: string, value: string): void {
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // ignore
    }
  }
  inMemoryCache.set(key, value)
}

/**
 * Validates and sanitizes a tab list against catalog keys, enforcing max 4 entries.
 */
function sanitizeTabList<T extends TabKey>(
  list: unknown,
  allowedKeys: readonly T[],
): T[] | undefined {
  if (!Array.isArray(list)) return undefined
  const validSet = new Set<T>(allowedKeys)
  const result: T[] = []
  for (const item of list) {
    if (result.length >= 4) break
    if (typeof item === 'string' && validSet.has(item as T) && !result.includes(item as T)) {
      result.push(item as T)
    }
  }
  return result
}

/**
 * Synchronous local cache reader for tab preferences.
 */
export function readTabPrefsLocalCache(uid: string, workspaceId: string): TabPrefs | null {
  try {
    const raw = getStorageItem(getStorageKey(uid, workspaceId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return {
      member: sanitizeTabList(parsed.member, MEMBER_TAB_KEYS),
      helper: sanitizeTabList(parsed.helper, HELPER_TAB_KEYS),
      updatedAt: parsed.updatedAt,
    }
  } catch {
    return null
  }
}

/**
 * Synchronous local cache writer for tab preferences.
 */
export function writeTabPrefsLocalCache(uid: string, workspaceId: string, prefs: TabPrefs): void {
  try {
    setStorageItem(getStorageKey(uid, workspaceId), JSON.stringify(prefs))
  } catch {
    // Storage quota or private browsing block
  }
}

/**
 * Clears tab preferences from local storage and memory (primarily for tests).
 */
export function clearTabPrefsLocalCache(): void {
  inMemoryCache.clear()
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i)
        if (key?.startsWith(STORAGE_KEY_PREFIX)) {
          keysToRemove.push(key)
        }
      }
      for (const key of keysToRemove) {
        window.localStorage.removeItem(key)
      }
    } catch {
      // ignore
    }
  }
}

/**
 * Subscribes to the user's tab preferences for a specific workspace.
 * Local-first: immediately emits cached value if available, then updates on Firestore snapshot.
 */
export function subscribeTabPrefs(
  uid: string,
  workspaceId: string,
  callback: (prefs: TabPrefs | null) => void,
): Unsubscribe {
  // Deliver cached value immediately if present
  const initial = readTabPrefsLocalCache(uid, workspaceId)
  if (initial) {
    callback(initial)
  }

  try {
    const userDocRef = doc(firestoreDb, 'users', uid, 'tabPrefs', workspaceId)
    return onSnapshot(
      userDocRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          if (!initial) callback(null)
          return
        }
        const data = snapshot.data() as Record<string, unknown>
        const cleanPrefs: TabPrefs = {
          member: sanitizeTabList(data.member, MEMBER_TAB_KEYS),
          helper: sanitizeTabList(data.helper, HELPER_TAB_KEYS),
          updatedAt: data.updatedAt,
        }
        writeTabPrefsLocalCache(uid, workspaceId, cleanPrefs)
        callback(cleanPrefs)
      },
      (err) => {
        console.warn(`[tabPrefsService] subscribeTabPrefs snapshot error: ${err.message}`)
        // If offline or permission error, local cache remains active
      },
    )
  } catch (err) {
    console.warn(`[tabPrefsService] subscribeTabPrefs initialization error: ${(err as Error).message}`)
    return () => {}
  }
}

/**
 * Saves personal tab preferences (TB-P8).
 * Local-first: immediately updates localStorage, then syncs to Firestore.
 * Last write wins.
 */
export async function saveTabPrefs(
  uid: string,
  workspaceId: string,
  prefs: Partial<TabPrefs>,
): Promise<void> {
  const current = readTabPrefsLocalCache(uid, workspaceId) ?? {}
  const merged: TabPrefs = {
    ...current,
    ...prefs,
    member: prefs.member !== undefined ? sanitizeTabList(prefs.member, MEMBER_TAB_KEYS) : current.member,
    helper: prefs.helper !== undefined ? sanitizeTabList(prefs.helper, HELPER_TAB_KEYS) : current.helper,
    updatedAt: new Date().toISOString(),
  }

  // 1. Write locally first
  writeTabPrefsLocalCache(uid, workspaceId, merged)

  // 2. Sync to Firestore
  try {
    const userDocRef = doc(firestoreDb, 'users', uid, 'tabPrefs', workspaceId)
    const firestorePayload: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    }
    if (merged.member !== undefined) firestorePayload.member = merged.member
    if (merged.helper !== undefined) firestorePayload.helper = merged.helper

    await setDoc(userDocRef, firestorePayload, { merge: true })
  } catch (error) {
    // Offline or network error: local copy is retained
    console.warn('[tabPrefsService] saveTabPrefs Firestore write deferred/failed:', error)
  }
}

/**
 * Subscribes to workspace tab defaults.
 */
export function subscribeWorkspaceTabDefaults(
  workspaceId: string,
  callback: (defaults: WorkspaceTabDefaults | null) => void,
): Unsubscribe {
  try {
    const wsRef = doc(firestoreDb, 'workspaces', workspaceId)
    return onSnapshot(
      wsRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null)
          return
        }
        const data = snapshot.data() as { tabDefaults?: WorkspaceTabDefaults }
        callback(data?.tabDefaults ?? null)
      },
      (err) => {
        console.warn(`[tabPrefsService] subscribeWorkspaceTabDefaults error: ${err.message}`)
      },
    )
  } catch (err) {
    console.warn(`[tabPrefsService] subscribeWorkspaceTabDefaults initialization error: ${(err as Error).message}`)
    return () => {}
  }
}

/**
 * Saves workspace tab defaults (Admin only).
 */
export async function saveWorkspaceTabDefaults(
  workspaceId: string,
  defaults: WorkspaceTabDefaults,
): Promise<void> {
  const wsRef = doc(firestoreDb, 'workspaces', workspaceId)
  const cleaned = stripUndefinedDeep(defaults) as WorkspaceTabDefaults
  const normalized: WorkspaceTabDefaults = {}

  if (cleaned.member) {
    const member: NonNullable<WorkspaceTabDefaults['member']> = {}
    for (const [role, list] of Object.entries(cleaned.member)) {
      if (Array.isArray(list) && list.length > 0) {
        member[role as keyof NonNullable<WorkspaceTabDefaults['member']>] = list
      }
    }
    if (Object.keys(member).length > 0) {
      normalized.member = member
    }
  }

  if (Array.isArray(cleaned.helper) && cleaned.helper.length > 0) {
    normalized.helper = cleaned.helper
  }

  if (cleaned.locks) {
    const locks: NonNullable<WorkspaceTabDefaults['locks']> = {}
    if (cleaned.locks.member) {
      const memberLocks: NonNullable<NonNullable<WorkspaceTabDefaults['locks']>['member']> = {}
      for (const [role, list] of Object.entries(cleaned.locks.member)) {
        if (Array.isArray(list) && list.length > 0) {
          memberLocks[role as keyof NonNullable<NonNullable<WorkspaceTabDefaults['locks']>['member']>] = list
        }
      }
      if (Object.keys(memberLocks).length > 0) {
        locks.member = memberLocks
      }
    }
    if (Array.isArray(cleaned.locks.helper) && cleaned.locks.helper.length > 0) {
      locks.helper = cleaned.locks.helper
    }
    if (Object.keys(locks).length > 0) {
      normalized.locks = locks
    }
  }

  await updateDoc(wsRef, {
    tabDefaults: normalized,
    updatedAt: serverTimestamp(),
  })
}
